import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Alert, Row, Col, Dropdown } from 'react-bootstrap';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import ModernPlaceAutocomplete from '../../../../core/common/ModernPlaceAutocomplete';
import type { Library } from '@googlemaps/js-api-loader';
import Swal from 'sweetalert2';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { MergedGroup, User } from '../booking';
import FeatherIcon from 'feather-icons-react';

interface UpdateMergedBookingsModalProps {
  show: boolean;
  onHide: () => void;
  mergedGroup: MergedGroup | null;
  onUpdateSuccess: () => void;
}

interface Vehicle {
  numberPlate: string;
  model: string;
}

// Extend MergedGroup interface if status is provided by backend
interface ExtendedMergedGroup extends MergedGroup {
  status?: string; // Optional to avoid TS2339 error
}

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  'AIzaSyC7S0DKmKpZmtidyMto2PiIXhR5miu9Pi0';
const GOOGLE_MAPS_LIBRARIES: Library[] = ['places'];
const center = { lat: 6.9271, lng: 79.8612 }; // Default center (Colombo, Sri Lanka)

const UpdateMergedBookingsModal: React.FC<UpdateMergedBookingsModalProps> = ({
  show,
  onHide,
  mergedGroup,
  onUpdateSuccess,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [suggestedDepartureTime, setSuggestedDepartureTime] = useState<string>('');
  const [suggestedArrivalTime, setSuggestedArrivalTime] = useState<string>('');
  const [selectedDriver, setSelectedDriver] = useState<User | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [locations, setLocations] = useState<{ address: string; lat: string; lng: string; orderId: number }[]>([]);
  const [drivers, setDrivers] = useState<User[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const autocompleteRefs = useRef<(google.maps.places.Autocomplete | null)[]>([]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Fetch available drivers and vehicles based on selected times
  useEffect(() => {
    const fetchDriversAndVehicles = async () => {
      if (!suggestedDepartureTime || !suggestedArrivalTime) return;

      const departureTime = new Date(suggestedDepartureTime).getTime();
      const arrivalTime = new Date(suggestedArrivalTime).getTime();
      const totalPassengers = mergedGroup?.bookings?.reduce(
        (sum, booking) => sum + (Number(booking.noOfPassengers) || 0),
        0
      ) || 0;

      try {
        const [driverResponse, vehicleResponse] = await Promise.all([
          apiDefinitions.getAvailableDrivers(departureTime, arrivalTime),
          apiDefinitions.getAvailableVehicles(departureTime, arrivalTime, totalPassengers),
        ]);
        setDrivers(driverResponse.data?.data?.drivers || []);
        setVehicles(vehicleResponse.data?.data || []);
      } catch (err: any) {
        setError(
          'Failed to fetch drivers or vehicles: ' + (err.response?.data?.statusDescription || err.message)
        );
      }
    };

    if (show && mergedGroup && suggestedDepartureTime && suggestedArrivalTime) {
      fetchDriversAndVehicles();
    }
  }, [show, mergedGroup, suggestedDepartureTime, suggestedArrivalTime]);

  // Initialize form with merged group data
  useEffect(() => {
    if (show && mergedGroup) {
      setSuggestedDepartureTime(
        mergedGroup.suggestedDepartureTime
          ? new Date(Number(mergedGroup.suggestedDepartureTime)).toISOString().slice(0, 16)
          : ''
      );
      setSuggestedArrivalTime(
        mergedGroup.suggestedArrivalTime
          ? new Date(Number(mergedGroup.suggestedArrivalTime)).toISOString().slice(0, 16)
          : ''
      );
      setSelectedDriver(mergedGroup.bookings?.[0]?.driver || null);
      setSelectedVehicle(mergedGroup.bookings?.[0]?.vehicle || null);
      setLocations(
        mergedGroup.bookings?.flatMap((b) => b.locations || []).map((loc, index) => ({
          address: loc.address || '',
          lat: loc.lat || '',
          lng: loc.lng || '',
          orderId: index + 1,
        })) || []
      );
      setError(null);
      setSuccess(null);
    }
  }, [show, mergedGroup]);

  const handleAddLocation = () => {
    setLocations([...locations, { address: '', lat: '', lng: '', orderId: locations.length + 1 }]);
  };

  const handleRemoveLocation = (index: number) => {
    setLocations(locations.filter((_, i) => i !== index).map((loc, i) => ({ ...loc, orderId: i + 1 })));
  };

  const handleLocationChange = (index: number, field: string, value: string) => {
    const newLocations = [...locations];
    newLocations[index] = { ...newLocations[index], [field]: value };
    setLocations(newLocations);
  };

  const handlePlaceSelect = (index: number) => {
    const autocomplete = autocompleteRefs.current[index];
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry?.location) {
        const newLocations = [...locations];
        newLocations[index] = {
          ...newLocations[index],
          address: place.formatted_address || '',
          lat: place.geometry.location.lat().toString(),
          lng: place.geometry.location.lng().toString(),
        };
        setLocations(newLocations);
      }
    }
  };

  const validateForm = () => {
    const now = Date.now();
    const departureTime = suggestedDepartureTime ? new Date(suggestedDepartureTime).getTime() : null;
    const arrivalTime = suggestedArrivalTime ? new Date(suggestedArrivalTime).getTime() : null;

    if (!departureTime) {
      return 'Departure time is required.';
    }
    if (departureTime <= now) {
      return 'Departure time must be in the future.';
    }
    if (arrivalTime && arrivalTime <= departureTime) {
      return 'Arrival time must be after departure time.';
    }
    if (locations.length < 2) {
      return 'At least two locations are required.';
    }
    if (locations.some((loc) => !loc.address || !loc.lat || !loc.lng)) {
      return 'All locations must have valid address, latitude, and longitude.';
    }
    // Conditionally check status if it exists
    const extendedGroup = mergedGroup as ExtendedMergedGroup;
    if (extendedGroup?.status === 'FINAL_APPROVED' && (!selectedDriver || !selectedVehicle)) {
      return 'Driver and vehicle are required for FINAL_APPROVED status.';
    }
    return null;
  };

  const handleUpdate = async () => {
    if (!mergedGroup?.groupId) {
      setError('No merged group selected.');
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {
      suggestedDepartureTime: suggestedDepartureTime
        ? new Date(suggestedDepartureTime).toISOString()
        : null,
      suggestedArrivalTime: suggestedArrivalTime ? new Date(suggestedArrivalTime).toISOString() : null,
      empNo: selectedDriver?.empNumber || null,
      numberPlate: selectedVehicle?.numberPlate || null,
      mergeGroupLocations: locations.map((loc) => ({
        address: loc.address,
        lat: loc.lat,
        lng: loc.lng,
        orderId: loc.orderId,
        createdAt: new Date().toISOString().replace('T', ' ').slice(0, 19),
      })),
    };

    setIsLoading(true);
    try {
      await apiDefinitions.updateMergedGroupDetails(mergedGroup.groupId, payload);
      setSuccess('Merged group updated successfully.');
      setError(null);
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Merged group updated successfully.',
        timer: 2000,
        showConfirmButton: false,
      });
      onUpdateSuccess();
      setTimeout(onHide, 2000);
    } catch (err: any) {
      const message =
        err.response?.data?.statusDescription ||
        err.message ||
        'Failed to update merged group.';
      setError(message);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      autoFocus={false}
      enforceFocus={false}
      aria-labelledby={`update-merged-group-${mergedGroup?.groupId || 'unknown'}`}
      className="font-sans"
    >
      <Modal.Header closeButton className="bg-gray-100 border-b border-gray-300">
        <Modal.Title
          id={`update-merged-group-${mergedGroup?.groupId || 'unknown'}`}
          className="text-lg font-semibold text-gray-800"
        >
          Update Merged Group - Group ID: {mergedGroup?.groupId || 'N/A'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        <Form>
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="text-sm font-medium text-gray-700">
                  Suggested Departure Time
                </Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={suggestedDepartureTime}
                  onChange={(e) => setSuggestedDepartureTime(e.target.value)}
                  aria-label="Suggested departure time"
                  className="border-gray-300 rounded-md shadow-sm"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="text-sm font-medium text-gray-700">
                  Suggested Arrival Time
                </Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={suggestedArrivalTime}
                  onChange={(e) => setSuggestedArrivalTime(e.target.value)}
                  aria-label="Suggested arrival time"
                  className="border-gray-300 rounded-md shadow-sm"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="text-sm font-medium text-gray-700">Driver</Form.Label>
                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-primary"
                    id="driver-dropdown"
                    className="w-full border-gray-300 rounded-md"
                  >
                    {selectedDriver
                      ? `${selectedDriver.name} (${selectedDriver.empNumber})`
                      : 'Select Driver'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-full max-h-60 overflow-y-auto">
                    {drivers.length > 0 ? (
                      drivers.map((driver) => (
                        <Dropdown.Item
                          key={driver.empNumber}
                          onClick={() => setSelectedDriver(driver)}
                          aria-label={`Select driver ${driver.name}`}
                        >
                          {driver.name} ({driver.empNumber})
                        </Dropdown.Item>
                      ))
                    ) : (
                      <Dropdown.Item disabled>No drivers available</Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="text-sm font-medium text-gray-700">Vehicle</Form.Label>
                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-primary"
                    id="vehicle-dropdown"
                    className="w-full border-gray-300 rounded-md"
                  >
                    {selectedVehicle
                      ? `${selectedVehicle.numberPlate} (${selectedVehicle.model})`
                      : 'Select Vehicle'}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="w-full max-h-60 overflow-y-auto">
                    {vehicles.length > 0 ? (
                      vehicles.map((vehicle) => (
                        <Dropdown.Item
                          key={vehicle.numberPlate}
                          onClick={() => setSelectedVehicle(vehicle)}
                          aria-label={`Select vehicle ${vehicle.numberPlate}`}
                        >
                          {vehicle.numberPlate} ({vehicle.model})
                        </Dropdown.Item>
                      ))
                    ) : (
                      <Dropdown.Item disabled>No vehicles available</Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </Form.Group>
            </Col>
          </Row>
          <h5 className="text-md font-semibold text-gray-800 mb-3">Locations</h5>
          {locations.map((location, index) => (
            <Row key={index} className="mb-3 items-center">
              <Col md={5}>
                <Form.Group>
                  <Form.Label className="text-sm font-medium text-gray-700">
                    Address {index + 1}
                  </Form.Label>
                  <ModernPlaceAutocomplete
                    value={location.address}
                    placeholder="Enter address"
                    onChange={(val) => handleLocationChange(index, 'address', val)}
                    onPlaceSelect={(address, lat, lng) => {
                      const newLocations = [...locations];
                      newLocations[index] = {
                        ...newLocations[index],
                        address,
                        lat,
                        lng,
                      };
                      setLocations(newLocations);
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="text-sm font-medium text-gray-700">Latitude</Form.Label>
                  <Form.Control
                    type="text"
                    value={location.lat}
                    onChange={(e) => handleLocationChange(index, 'lat', e.target.value)}
                    placeholder="Latitude"
                    aria-label={`Latitude ${index + 1}`}
                    className="border-gray-300 rounded-md shadow-sm"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="text-sm font-medium text-gray-700">Longitude</Form.Label>
                  <Form.Control
                    type="text"
                    value={location.lng}
                    onChange={(e) => handleLocationChange(index, 'lng', e.target.value)}
                    placeholder="Longitude"
                    aria-label={`Longitude ${index + 1}`}
                    className="border-gray-300 rounded-md shadow-sm"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={1} className="flex items-end">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleRemoveLocation(index)}
                  aria-label={`Remove location ${index + 1}`}
                  disabled={locations.length <= 2}
                  className="p-2"
                >
                  <FeatherIcon icon="trash-2" size={16} />
                </Button>
              </Col>
            </Row>
          ))}
          <Button
            variant="outline-primary"
            onClick={handleAddLocation}
            className="mb-4 flex items-center space-x-2"
            aria-label="Add new location"
          >
            <FeatherIcon icon="plus-circle" size={16} />
            <span>Add Location</span>
          </Button>
          {isLoaded && (
            <GoogleMap
              mapContainerStyle={{ height: '300px', width: '100%' }}
              center={center}
              zoom={12}
              onLoad={(map) => {
                mapRef.current = map;
              }}
              options={{ disableDefaultUI: true, zoomControl: true }}
            >
              {locations
                .filter((loc) => loc.lat && loc.lng)
                .map((loc, index) => (
                  <Marker
                    key={index}
                    position={{ lat: parseFloat(loc.lat), lng: parseFloat(loc.lng) }}
                    title={`Location ${index + 1}`}
                    label={`${index + 1}`}
                  />
                ))}
            </GoogleMap>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer className="bg-gray-100 border-t border-gray-300">
        <Button
          variant="outline-secondary"
          onClick={onHide}
          disabled={isLoading}
          className="flex items-center space-x-2"
          aria-label="Close update modal"
        >
          <FeatherIcon icon="x-circle" size={18} />
          <span>Close</span>
        </Button>
        <Button
          variant="primary"
          onClick={handleUpdate}
          disabled={isLoading}
          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white"
          aria-label="Update merged group"
        >
          <FeatherIcon icon="save" size={18} />
          <span>{isLoading ? 'Updating...' : 'Update'}</span>
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UpdateMergedBookingsModal;